import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle2, Sparkles, ShieldCheck, Zap, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import SoftAurora from '../components/SoftAurora';
import { LegalFooter } from '../components/LegalFooter';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { toast } from 'sonner';

const DEFAULT_PILOT_CONFIG = {
  demo_request_enabled: true,
  invite_only_signup: false,
  app_login_url: '',
  expose_dev_codes: false,
  production: false,
};

function SignInOrbit({ large = false }) {
  return (
    <span className={large ? 'aerta-signin-orbit aerta-signin-orbit--lg' : 'aerta-signin-orbit'} aria-hidden>
      <span className="aerta-signin-orbit__ring" />
      <span className="aerta-signin-orbit__dot" />
    </span>
  );
}

export function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pilotConfig, setPilotConfig] = useState(DEFAULT_PILOT_CONFIG);
  const [magicLinkRequired, setMagicLinkRequired] = useState(false);
  const [magicLinkSending, setMagicLinkSending] = useState(false);
  const [consumingMagic, setConsumingMagic] = useState(false);
  const [consumingPickup, setConsumingPickup] = useState(false);
  const [devMagicLinkUrl, setDevMagicLinkUrl] = useState(null);

  const magicEffectRan = useRef(false);
  const pickupEffectRan = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { login, register, user, requestMagicLink, consumeMagicLink } = useAuth();
  const navigate = useNavigate();
  const prefersReducedMotion = usePrefersReducedMotion();
  const trustHighlights = [
    { icon: ShieldCheck, label: 'Evidence-ready reporting' },
    { icon: Zap, label: 'Scoped deployment' },
    { icon: Sparkles, label: 'Pilot in weeks' },
  ];

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/public/pilot-config')
      .then((cfg) => {
        if (!cancelled && cfg) setPilotConfig({ ...DEFAULT_PILOT_CONFIG, ...cfg });
      })
      .catch(() => {
        if (!cancelled) setPilotConfig(DEFAULT_PILOT_CONFIG);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const magic = searchParams.get('magic');
    if (!magic || magicEffectRan.current) return;
    magicEffectRan.current = true;
    setConsumingMagic(true);
    consumeMagicLink(magic.trim())
      .then(() => {
        toast.success('Signed in with your email link.');
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        magicEffectRan.current = false;
        toast.error(err.message || 'This sign-in link is invalid or expired.');
        setSearchParams({}, { replace: true });
      })
      .finally(() => setConsumingMagic(false));
  }, [searchParams, consumeMagicLink, navigate, setSearchParams]);

  useEffect(() => {
    const pickup = searchParams.get('pickup');
    if (!pickup || pickupEffectRan.current) return;
    pickupEffectRan.current = true;
    setConsumingPickup(true);
    api
      .post('/api/public/invite-pickup', { token: pickup.trim() })
      .then((data) => {
        if (data.invite_code) {
          setInviteCode(data.invite_code);
          if (data.email) setEmail(data.email);
          setIsLogin(false);
          toast.success('Invite code loaded from your link. Complete signup below.');
        }
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('pickup');
          return next;
        }, { replace: true });
      })
      .catch((err) => {
        pickupEffectRan.current = false;
        toast.error(err.message || 'Invalid or expired invite link.');
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('pickup');
          return next;
        }, { replace: true });
      })
      .finally(() => setConsumingPickup(false));
  }, [searchParams, setSearchParams]);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const inviteOnly = !!pilotConfig.invite_only_signup;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isLogin) {
      if (name.trim().length < 2) {
        setError('Please enter your full name.');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      if (!agreeToTerms) {
        setError('Please accept the pilot terms to continue.');
        return;
      }
      if (inviteOnly && !inviteCode.trim()) {
        setError('Enter the pilot invite code from your welcome email.');
        return;
      }
    }

    setLoading(true);
    setMagicLinkRequired(false);
    setDevMagicLinkUrl(null);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back to AERTA!');
      } else {
        await register(email, password, name, inviteOnly || inviteCode.trim() ? inviteCode : undefined);
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } catch (err) {
      if (err.code === 'MAGIC_LINK_REQUIRED') {
        setMagicLinkRequired(true);
      }
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMagicLink = async () => {
    const addr = email.trim();
    if (!addr) {
      toast.error('Enter the email address for this account first.');
      return;
    }
    setMagicLinkSending(true);
    setDevMagicLinkUrl(null);
    try {
      const res = await requestMagicLink(addr);
      if (res?.magic_link_url) {
        setDevMagicLinkUrl(res.magic_link_url);
        toast.success('No SMTP — use the sign-in link below (local dev only).');
      } else {
        toast.success('If an account exists for that email, you will receive a sign-in link shortly.');
      }
    } catch (err) {
      toast.error(err.message || 'Could not send sign-in link.');
    } finally {
      setMagicLinkSending(false);
    }
  };

  const textOnAurora =
    '[text-shadow:0_2px_20px_rgba(0,0,0,0.9),0_1px_3px_rgba(0,0,0,0.95)]';

  const landing = !prefersReducedMotion;

  if (consumingMagic) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050508]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-zinc-300">Signing you in…</p>
        </div>
      </div>
    );
  }

  if (consumingPickup) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050508]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-zinc-300">Loading your invite…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen overflow-x-hidden bg-[#050508] dark:bg-background">
      {/* SoftAurora — slightly softened so peaks do not blow out typography */}
      {!prefersReducedMotion ? (
        <div
          className={`pointer-events-none absolute inset-0 z-0 ${landing ? 'aerta-login-scene-fade' : ''}`}
          aria-hidden
        >
          <div className="pointer-events-none absolute inset-0 origin-center will-change-[opacity,transform] animate-aurora-alive motion-reduce:animate-none">
            <SoftAurora
              speed={0.52}
              scale={1.48}
              brightness={0.82}
              color1="#e4e8f4"
              color2="#c026d3"
              noiseFrequency={2.35}
              noiseAmplitude={0.9}
              bandHeight={0.41}
              bandSpread={0.96}
              octaveDecay={0.11}
              layerOffset={0.03}
            colorSpeed={0.88}
            enableMouseInteraction={false}
            mouseInfluence={0.22}
            />
          </div>
        </div>
      ) : (
        <div
          className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-[#08080c] via-[#0c0c14] to-[#1a1025]"
          aria-hidden
        />
      )}

      {/* Atmosphere: drifting orbs + grid + grain + vignettes (readability) */}
      <div
        className={`pointer-events-none absolute inset-0 z-[1] ${landing ? 'aerta-login-scene-fade' : ''}`}
        aria-hidden
      >
        <div className="absolute -left-[18%] top-[6%] h-[min(520px,68vh)] w-[min(520px,52vw)] rounded-full bg-fuchsia-600/30 blur-[100px] motion-safe:animate-login-orb-a motion-reduce:translate-x-0 motion-reduce:opacity-80" />
        <div className="absolute -right-[12%] bottom-[8%] h-[min(480px,60vh)] w-[min(480px,48vw)] rounded-full bg-blue-600/25 blur-[100px] motion-safe:animate-login-orb-b motion-reduce:opacity-75" />
        <div className="absolute left-[28%] top-[38%] h-[min(360px,45vh)] w-[min(360px,40vw)] rounded-full bg-cyan-500/15 blur-[90px] motion-safe:animate-login-orb-c motion-reduce:opacity-60" />
        <div className="aerta-login-grid absolute inset-0 opacity-50 motion-safe:animate-login-grid-pulse motion-reduce:opacity-[0.04]" />
        <div className="aerta-login-grain absolute inset-0" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_95%_68%_at_50%_48%,transparent_0%,rgba(0,0,0,0.26)_45%,rgba(0,0,0,0.58)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/55" />
        <div className="absolute inset-y-0 left-0 hidden w-[52%] max-w-xl bg-gradient-to-r from-black/82 via-black/42 to-transparent lg:block" />
        <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/55 to-transparent lg:hidden" />
      </div>

      <div className="relative z-10 flex min-h-screen w-full flex-col lg:flex-row">
        {/* Left — copy sits on the aurora (read lane + text shadow); no boxed panel */}
        <div
          className={`relative hidden overflow-hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-center lg:px-10 xl:px-14 ${landing ? 'aerta-login-hero-enter' : ''}`}
        >
          <div className="relative z-10 mx-auto flex w-full max-w-md flex-col justify-center py-8 xl:py-10">
            <div className="mb-8 flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 scale-110 rounded-full bg-primary/25 blur-xl motion-safe:animate-pulse motion-reduce:animate-none" aria-hidden />
                <img
                  src="/aerta-logo.png"
                  alt="AERTA logo"
                  className="relative h-12 w-12 object-contain drop-shadow-[0_8px_28px_rgba(59,130,246,0.55)]"
                />
              </div>
              <div>
                <h1 className="font-heading bg-gradient-to-r from-white via-zinc-100 to-zinc-400 bg-clip-text text-4xl font-black tracking-tight text-transparent drop-shadow-[0_2px_24px_rgba(0,0,0,0.75)]">
                  AERTA
                </h1>
                <p className={`text-sm tracking-wide text-zinc-300 ${textOnAurora}`}>
                  Autonomous red &amp; blue validation
                </p>
                <p className="mt-1 text-[11px] leading-snug text-zinc-500">
                  Advanced Enterprise Risk Threat Analysis
                </p>
              </div>
            </div>
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400/95 drop-shadow-[0_0_18px_rgba(56,189,248,0.35)]">
              Measurable red vs. blue validation
            </p>
            <h2 className="mb-4 bg-gradient-to-br from-white via-sky-100 to-fuchsia-300 bg-clip-text font-heading text-4xl font-bold leading-[1.12] text-transparent drop-shadow-[0_4px_32px_rgba(0,0,0,0.45)] xl:text-[2.45rem]">
              Continuous Discovery &amp; Risk Validation
            </h2>
            <p className={`max-w-md text-lg leading-relaxed text-zinc-200 ${textOnAurora}`}>
              Prioritize with intel-backed risk scoring. Run autonomous red vs. blue exercises on your attack surface
              (human approval gates for offensive steps where configured),
              validate defenses, and ship executive-ready reporting—without waiting on the annual pentest cycle alone.
            </p>
            <a
              href="/platform"
              className="mt-8 inline-flex text-sm font-medium text-sky-400/90 underline-offset-4 transition-colors hover:text-sky-300 hover:underline"
            >
              Full platform overview — capabilities &amp; use cases →
            </a>
            <div className="mt-10 grid grid-cols-2 gap-4">
              <div className="group rounded-2xl bg-gradient-to-br from-red-950/50 to-red-950/5 p-4 shadow-[0_0_40px_-12px_rgba(239,68,68,0.25)] backdrop-blur-[2px] transition-shadow duration-500 group-hover:shadow-[0_0_48px_-8px_rgba(239,68,68,0.35)]">
                <p className={`font-heading font-bold text-red-400 ${textOnAurora}`}>Red Team AI</p>
                <p className={`mt-1 text-sm text-zinc-300 ${textOnAurora}`}>Attack emulation</p>
              </div>
              <div className="group rounded-2xl bg-gradient-to-br from-blue-950/50 to-blue-950/5 p-4 shadow-[0_0_40px_-12px_rgba(59,130,246,0.22)] backdrop-blur-[2px] transition-shadow duration-500 group-hover:shadow-[0_0_48px_-8px_rgba(59,130,246,0.32)]">
                <p className={`font-heading font-bold text-blue-400 ${textOnAurora}`}>Blue Team AI</p>
                <p className={`mt-1 text-sm text-zinc-300 ${textOnAurora}`}>Detection &amp; response</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form (gradient halo + dense glass; no hard stroke) */}
        <div className="relative flex min-h-[50vh] flex-1 items-center justify-center p-6 md:min-h-0 md:p-8 lg:w-1/2 lg:flex-none lg:px-10 xl:px-14">
          <div className={`relative w-full max-w-md ${landing ? 'aerta-login-form-enter' : ''}`}>
            <div
              className="pointer-events-none absolute -inset-8 rounded-[2rem] bg-gradient-to-br from-sky-500/20 via-violet-600/15 to-fuchsia-600/20 opacity-70 blur-3xl motion-reduce:opacity-40"
              aria-hidden
            />
            <div className="relative rounded-2xl bg-gradient-to-br from-white/[0.14] via-indigo-500/25 to-fuchsia-500/20 p-[1px] shadow-[0_0_120px_-32px_rgba(139,92,246,0.55),0_40px_100px_-40px_rgba(0,0,0,0.85)]">
              <Card className="relative z-10 w-full rounded-[15px] border-0 bg-zinc-950/[0.93] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] backdrop-blur-2xl dark:bg-zinc-950/95">
            <CardHeader className="space-y-2 text-center">
              <div className="mb-1 flex items-center justify-center gap-2 lg:hidden">
                <img
                  src="/aerta-logo.png"
                  alt="AERTA logo"
                  className="h-9 w-9 object-contain drop-shadow-[0_4px_14px_rgba(59,130,246,0.25)]"
                />
                <div className="text-left">
                  <p className="font-heading text-sm font-semibold text-foreground">AERTA</p>
                  <p className="text-[10px] leading-tight text-muted-foreground">Autonomous red &amp; blue validation</p>
                </div>
              </div>
            <CardTitle className="text-2xl font-heading">
              {isLogin ? 'Sign in to AERTA' : 'Create Account'}
            </CardTitle>
            <CardDescription className="space-y-2">
              <span className="block">
                {isLogin
                  ? "Welcome — we're glad you're here. Access dashboards, risk views, and reporting for your scoped pilot."
                  : inviteOnly
                    ? 'Use the same work email as your demo request and the invite code from your welcome email.'
                    : 'Welcome — create a pilot account. Scoped deployment and evidence exports align with your success criteria.'}
              </span>
              <span className="block text-xs text-muted-foreground/90">From the team at Aethereal</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
              {trustHighlights.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] text-zinc-300 shadow-[0_0_24px_-8px_rgba(59,130,246,0.2)] backdrop-blur-md transition-colors hover:border-primary/25 hover:text-foreground"
                >
                  <Icon className="h-3 w-3 text-sky-400" />
                  {label}
                </span>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Full name</label>
                  <Input
                    type="text"
                    placeholder="Jane Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              )}

              {!isLogin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Pilot invite code {inviteOnly ? '' : '(optional)'}
                  </label>
                  <Input
                    type="text"
                    placeholder="AERTA-XXXXXX-XXXXXX"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    required={inviteOnly}
                    autoComplete="off"
                    data-testid="invite-code-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {inviteOnly
                      ? 'One code registers one account. After signup, sign in on any device with email and password.'
                      : 'If you received a welcome email, paste your code here to link the pilot invite to this account.'}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Email</label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {!isLogin && (
                  <p className="text-xs text-muted-foreground">
                    Use at least 8 characters. Include a number for stronger security.
                  </p>
                )}
              </div>

              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Confirm password</label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <label className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5"
                      required
                    />
                    <span>
                      I agree to the{' '}
                      <a href="/legal/terms" className="text-primary underline-offset-2 hover:underline">
                        Terms of Use
                      </a>{' '}
                      and{' '}
                      <a href="/legal/privacy" className="text-primary underline-offset-2 hover:underline">
                        Privacy Policy
                      </a>
                      , and I understand this pilot environment may be reset periodically.
                    </span>
                  </label>
                </>
              )}

              {error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                  </div>
                </div>
              )}

              {isLogin && magicLinkRequired && (
                <div className="rounded-md border border-sky-500/35 bg-sky-500/10 p-3 text-sm text-sky-100">
                  <p className="mb-3 font-medium text-sky-50">Password sign-in is paused for this account.</p>
                  <p className="mb-3 text-sm text-sky-100/95">
                    Locked out or need a one-time link? Use the email field above, then request a link.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full gap-2 border-sky-500/40 bg-sky-950/50 text-sky-50 hover:bg-sky-900/60"
                    disabled={magicLinkSending}
                    onClick={handleRequestMagicLink}
                  >
                    {magicLinkSending ? <SignInOrbit /> : <Mail className="h-4 w-4" />}
                    Email sign-in link
                  </Button>
                  <p className="mt-2 text-xs text-sky-200/80">Check spam folders if nothing arrives.</p>
                  {devMagicLinkUrl ? (
                    <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-950/50 p-2.5 text-left">
                      <p className="mb-1.5 text-[11px] font-medium text-amber-200">
                        No SMTP — open this link to sign in (local dev only):
                      </p>
                      <a
                        href={devMagicLinkUrl}
                        className="break-all text-sm text-sky-300 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {devMagicLinkUrl}
                      </a>
                    </div>
                  ) : null}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 font-semibold tracking-wide text-primary-foreground shadow-[0_4px_24px_-4px_rgba(59,130,246,0.55)] transition-all duration-300 hover:from-blue-500/95 hover:to-blue-600/95 hover:shadow-[0_8px_32px_-6px_rgba(59,130,246,0.5)]"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <SignInOrbit />
                    <span className="tabular-nums">{isLogin ? 'Signing in…' : 'Creating account…'}</span>
                  </span>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>

              {!isLogin && (
                <div className="rounded-md border border-success/30 bg-success/10 px-3 py-2 text-xs text-muted-foreground">
                  <p className="mb-1 flex items-center gap-1.5 font-medium text-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                    What happens next
                  </p>
                  <p>Your account is created instantly and you will be signed in automatically.</p>
                </div>
              )}
            </form>

            <div className="relative z-[100] mt-6 space-y-3 text-center">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setMagicLinkRequired(false);
                  setDevMagicLinkUrl(null);
                  setConfirmPassword('');
                  setInviteCode('');
                  setAgreeToTerms(false);
                }}
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
              <div>
                <a
                  href="/platform"
                  className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-primary hover:underline"
                >
                  Platform overview — AI prediction, monitoring &amp; defense workflows
                </a>
              </div>
              <LegalFooter className="pt-2 text-zinc-500 [&_a]:text-zinc-400 [&_a:hover]:text-sky-400" />
            </div>
          </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
